// src/components/categories/Category.js
import React, { useState, useEffect, useRef } from "react";
import {
    Edit,
    Trash2,
    Plus,
    X,
    ChevronDown,
    ChevronRight,
    Percent,
    Image as ImageIcon,
    AlertCircle,
    CheckCircle,
    Move,
} from "lucide-react";
import apiWithAuth from "../../services/apiWithAuth";
import { usePermissions } from "../../hooks/usePermissions";

// default empty category used for "add new"
const defaultCategory = {
    name: "",
    imageUrl: "",
    parentId: null,
    isActive: true,
    gstRate: 0,
    description: "",
    order: 0,
};

// Confirmation modal component
function ConfirmationModal({
    isOpen,
    onClose,
    onConfirm,
    title,
    children,
    requireConfirmationText = false,
}) {
    const [confirmationText, setConfirmationText] = useState("");
    useEffect(() => {
        if (isOpen) setConfirmationText("");
    }, [isOpen]);
    const isConfirmed = !requireConfirmationText || confirmationText.toLowerCase() === "confirm";
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 sm:p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg m-4">
                <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                    <div className="mt-4 text-gray-600 whitespace-pre-wrap">{children}</div>
                    {requireConfirmationText && (
                        <div className="mt-4">
                            <label htmlFor="confirmation-input" className="block text-sm text-gray-700 mb-1">
                                Type <strong>confirm</strong> to proceed
                            </label>
                            <input
                                id="confirmation-input"
                                type="text"
                                value={confirmationText}
                                onChange={(e) => setConfirmationText(e.target.value)}
                                className="w-full px-3 py-2 border rounded"
                                placeholder="confirm"
                            />
                        </div>
                    )}
                </div>
                <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-lg">
                    <button onClick={onClose} className="px-4 py-2 rounded bg-gray-200">
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-4 py-2 rounded bg-red-600 text-white disabled:opacity-50"
                        disabled={!isConfirmed}
                    >
                        Confirm
                    </button>
                </div>
            </div>
        </div>
    );
}

// simple toggle switch
function ToggleSwitch({ checked, onChange, disabled }) {
    return (
        <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} className="sr-only" />
            <div className="w-11 h-6 bg-gray-300 rounded-full relative">
                <div className={`absolute top-[2px] left-[2px] h-5 w-5 bg-white rounded-full transition-transform ${checked ? "translate-x-5" : ""}`} />
            </div>
        </label>
    );
}

// helper to collect all descendants (used for confirmation text)
function getDescendantCategories(category) {
    const descendants = [];
    if (category.children && category.children.length) {
        for (const c of category.children) {
            descendants.push(c);
            descendants.push(...getDescendantCategories(c));
        }
    }
    return descendants;
}

export default function CategoryAdminPanel() {
    const permissions = usePermissions("Categories");
    const [categories, setCategories] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 1 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState("");
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [newCategory, setNewCategory] = useState(defaultCategory);
    const [editingCategory, setEditingCategory] = useState(null);
    const [expandedCategories, setExpandedCategories] = useState({});
    const [uploadLoading, setUploadLoading] = useState(false);
    const [togglingCategory, setTogglingCategory] = useState(null);
    const [confirmationModal, setConfirmationModal] = useState({ isOpen: false, title: "", message: "", onConfirm: () => { } });

    // reorder states
    const [reorderMode, setReorderMode] = useState(false);
    const [dragSourceId, setDragSourceId] = useState(null);
    const [dragOverId, setDragOverId] = useState(null);
    const [reorderingSaving, setReorderingSaving] = useState(false);

    const fileInputRef = useRef(null);
    const editFileInputRef = useRef(null);

    // Fetch categories (server returns hierarchical tree)
    const fetchCategories = async (page = pagination.page) => {
        setLoading(true);
        setError(null);
        try {
            const resp = await apiWithAuth.get(`/admin-panel/categories?page=${page}&limit=${pagination.limit}`);
            if (resp.data.success) {
                setCategories(resp.data.data || []);
                setPagination((p) => ({ ...p, total: resp.data.pagination?.total || 0, totalPages: resp.data.pagination?.totalPages || 1, page: resp.data.pagination?.page || page }));
            } else {
                throw new Error(resp.data.message || "Failed to fetch categories");
            }
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || err.message || "Failed to fetch categories");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.page]);

    useEffect(() => {
        if (successMessage) {
            const t = setTimeout(() => setSuccessMessage(""), 4000);
            return () => clearTimeout(t);
        }
    }, [successMessage]);

    // toggle expand helper
    const toggleExpand = (categoryId) => {
        setExpandedCategories((prev) => ({ ...prev, [categoryId]: !prev[categoryId] }));
    };

    // find category in tree by id
    const findCategoryById = (id, arr = categories) => {
        for (const c of arr) {
            if (String(c._id) === String(id)) return c;
            if (c.children && c.children.length) {
                const found = findCategoryById(id, c.children);
                if (found) return found;
            }
        }
        return null;
    };

    // get siblings for a given parentId (based on current UI tree)
    const getSiblingsForParent = (parentId) => {
        if (!parentId) {
            // top-level siblings are those with no parentId
            return categories.filter((c) => !c.parentId);
        }
        const parent = findCategoryById(parentId);
        return parent?.children ? [...parent.children] : [];
    };

    // Upload image helper (returns file url or null)
    const uploadImage = async (file) => {
        if (!file) return null;
        setUploadLoading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await apiWithAuth.post("/upload/single", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            if (res.data.success) return res.data.file_url;
            throw new Error(res.data.message || "Upload failed");
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || err.message || "Image upload failed");
            return null;
        } finally {
            setUploadLoading(false);
        }
    };

    // Save new category
    const saveNewCategory = async (e) => {
        e?.preventDefault();
        setLoading(true);
        setError(null);
        try {
            let imageUrl = newCategory.imageUrl;
            if (newCategory.__file) {
                const url = await uploadImage(newCategory.__file);
                if (url) imageUrl = url;
            }
            const payload = {
                name: newCategory.name,
                description: newCategory.description,
                imageUrl,
                parentId: newCategory.parentId || null,
                isActive: newCategory.isActive,
                gstRate: Number(newCategory.gstRate || 0),
            };
            const res = await apiWithAuth.post("/admin-panel/categories", payload);
            if (res.data.success) {
                setSuccessMessage("Category created");
                setNewCategory(defaultCategory);
                setIsAddingNew(false);
                await fetchCategories();
            } else {
                throw new Error(res.data.message || "Failed to create");
            }
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || err.message || "Failed to create category");
        } finally {
            setLoading(false);
        }
    };

    // Update existing category
    const updateCategory = async (e) => {
        e?.preventDefault();
        if (!editingCategory || !editingCategory._id) return;
        setLoading(true);
        setError(null);
        try {
            let imageUrl = editingCategory.imageUrl;
            if (editingCategory.__file) {
                const url = await uploadImage(editingCategory.__file);
                if (url) imageUrl = url;
            }
            const payload = {
                name: editingCategory.name,
                description: editingCategory.description,
                imageUrl,
                parentId: editingCategory.parentId || null,
                isActive: editingCategory.isActive,
                gstRate: Number(editingCategory.gstRate || 0),
            };
            if (typeof editingCategory.order !== "undefined") payload.order = editingCategory.order;

            const res = await apiWithAuth.put(`/admin-panel/categories/${editingCategory._id}`, payload);
            if (res.data.success) {
                setSuccessMessage("Category updated");
                setEditingCategory(null);
                await fetchCategories();
            } else {
                throw new Error(res.data.message || "Failed to update");
            }
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || err.message || "Failed to update category");
        } finally {
            setLoading(false);
        }
    };

    // Toggle active/inactive (calls backend toggle endpoint)
    const toggleCategoryStatus = async (category, newStatus) => {
        setTogglingCategory(category._id);
        setError(null);
        try {
            const res = await apiWithAuth.patch("/admin-panel/categories/toggle", { categoryId: category._id, isActive: newStatus });
            if (res.data.success) {
                setSuccessMessage(`Category ${newStatus ? "activated" : "deactivated"}`);
                await fetchCategories();
            } else throw new Error(res.data.message || "Failed to toggle");
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || err.message || "Failed to toggle category");
        } finally {
            setTogglingCategory(null);
        }
    };

    // Delete category
    const deleteCategory = async (categoryId) => {
        setLoading(true);
        setError(null);
        try {
            const res = await apiWithAuth.delete(`/admin-panel/categories/${categoryId}`);
            if (res.data.success) {
                setSuccessMessage("Category deleted");
                await fetchCategories();
            } else throw new Error(res.data.message || "Failed to delete");
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || err.message || "Failed to delete category");
        } finally {
            setLoading(false);
        }
    };

    // ---------- Bulk reorder helper ----------
    // items: [{ id, order, parentId }, ...]
    const saveBulkOrder = async (items) => {
        setError(null);
        try {
            const resp = await apiWithAuth.post("/admin-panel/categories/reorder", { items });
            if (resp.data && resp.data.success) {
                setSuccessMessage("Order saved");
                await fetchCategories();
            } else {
                throw new Error(resp.data?.message || "Failed to save order");
            }
        } catch (err) {
            console.error("saveBulkOrder error", err);
            setError(err.response?.data?.message || err.message || "Failed to save order");
            // revert optimistic
            await fetchCategories();
        } finally {
            setReorderingSaving(false);
        }
    };

    // Reorder: drag start / over / drop (HTML5 DnD)
    const onDragStart = (e, categoryId) => {
        if (!reorderMode) return;
        e.dataTransfer.effectAllowed = "move";
        try {
            e.dataTransfer.setData("text/plain", categoryId);
        } catch (err) {
            // ignore browsers that block setData in some contexts
        }
        setDragSourceId(categoryId);
    };

    const onDragOver = (e, overCategoryId) => {
        if (!reorderMode) return;
        e.preventDefault();
        setDragOverId(overCategoryId);
    };

    const onDrop = async (e, overCategoryId, parentIdOfSiblings) => {
        if (!reorderMode) return;
        e.preventDefault();
        const sourceId = dragSourceId || (e.dataTransfer && e.dataTransfer.getData("text/plain"));
        const destId = overCategoryId;
        if (!sourceId || !destId) return;
        if (String(sourceId) === String(destId)) return;

        // Get siblings (current UI state)
        const siblings = getSiblingsForParent(parentIdOfSiblings);
        if (!siblings || siblings.length === 0) return;

        const srcIndex = siblings.findIndex((s) => String(s._id) === String(sourceId));
        const destIndex = siblings.findIndex((s) => String(s._id) === String(destId));
        if (srcIndex === -1 || destIndex === -1) return; // not same group

        const newSiblings = [...siblings];
        const [moved] = newSiblings.splice(srcIndex, 1);
        newSiblings.splice(destIndex, 0, moved);

        // prepare changed list only for those whose order changed
        const changed = [];
        newSiblings.forEach((cat, idx) => {
            const newOrder = idx + 1;
            if ((cat.order || 0) !== newOrder) changed.push({ id: cat._id, order: newOrder });
        });

        if (changed.length === 0) {
            setDragSourceId(null);
            setDragOverId(null);
            return;
        }

        // optimistic UI update: update the tree locally
        const updatedTree = JSON.parse(JSON.stringify(categories));
        const applyNewOrderToTree = (arr) => {
            return arr.map((node) => {
                if (String(node._id) === String(parentIdOfSiblings)) {
                    node.children = newSiblings.map((s, idx) => {
                        const existing = findCategoryById(s._id);
                        return existing ? { ...existing, order: idx + 1 } : { ...s, order: idx + 1 };
                    });
                } else if (node.children && node.children.length) {
                    node.children = applyNewOrderToTree(node.children);
                }
                return node;
            });
        };

        let newTree;
        if (!parentIdOfSiblings) {
            // top-level reorder: rebuild top-level array
            const otherNodes = updatedTree.filter((n) => n.parentId);
            const topLevelNodes = newSiblings.map((s, idx) => {
                const existing = categories.find((c) => String(c._id) === String(s._id));
                return existing ? { ...existing, order: idx + 1 } : { ...s, order: idx + 1 };
            });
            newTree = [...topLevelNodes, ...otherNodes];
        } else {
            newTree = applyNewOrderToTree(updatedTree);
        }
        setCategories(newTree);
        setDragSourceId(null);
        setDragOverId(null);
        setReorderingSaving(true);

        // Build items payload (single bulk call)
        const items = changed.map((it) => ({ id: it.id, order: it.order, parentId: parentIdOfSiblings || null }));

        // call bulk save
        await saveBulkOrder(items);
    };

    // toggle reorder mode
    const toggleReorderMode = () => {
        setReorderMode((prev) => {
            const next = !prev;
            if (!next) {
                // leaving -> refresh canonical state
                fetchCategories();
            } else {
                setDragSourceId(null);
                setDragOverId(null);
            }
            return next;
        });
    };

    // Render single category (recursive)
    const renderCategory = (category, depth = 0) => {
        const isExpanded = !!expandedCategories[category._id];
        const parentId = category.parentId || null;
        return (
            <div key={category._id} className={`rounded-md mb-3 border ${category.isActive ? "bg-white" : "bg-gray-100"} shadow-sm`}>
                <div
                    className={`flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4 p-3 sm:p-4 ${reorderMode ? "bg-yellow-50/30" : "hover:bg-gray-50"}`}
                    style={{ paddingLeft: `${12 + depth * 16}px` }}
                    draggable={reorderMode}
                    onDragStart={(e) => onDragStart(e, category._id)}
                    onDragOver={(e) => onDragOver(e, category._id)}
                    onDrop={(e) => onDrop(e, category._id, parentId)}
                >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                    {reorderMode ? (
                        <div className="p-2 rounded cursor-grab flex-shrink-0" title="Drag to reorder">
                            <Move size={16} />
                        </div>
                    ) : category.children && category.children.length ? (
                        <button className="p-2 rounded hover:bg-gray-100 flex-shrink-0" onClick={() => toggleExpand(category._id)}>
                            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </button>
                    ) : (
                        <div className="w-9 flex-shrink-0" />
                    )}

                    <div className="flex items-center gap-3 flex-1 min-w-0">
                        {category.imageUrl ? (
                            <img src={category.imageUrl} alt={category.name} className="h-10 w-10 sm:h-12 sm:w-12 rounded-md object-cover border flex-shrink-0" />
                        ) : (
                            <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0">
                                <ImageIcon size={18} />
                            </div>
                        )}

                        <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                                <div className="font-semibold text-gray-900 truncate">{category.name}</div>
                                <div className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-700 flex-shrink-0">{category.isActive ? "ACTIVE" : "INACTIVE"}</div>
                            </div>
                            <div className="mt-1 text-xs text-gray-600 flex flex-wrap items-center gap-2 sm:gap-4">
                                <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-50 border text-green-800">
                                    <Percent size={12} />
                                    <span className="font-semibold">Fixed Rate:</span>
                                    <span>{category.gstRate ?? 0}%</span>
                                </div>
                                <div className="text-gray-500">Order: {category.order ?? "-"}</div>
                            </div>
                        </div>
                    </div>
                    </div>

                    <div className="flex items-center justify-end gap-2 w-full sm:w-auto flex-shrink-0 pl-9 sm:pl-0">
                        <div>
                            <ToggleSwitch
                                checked={!!category.isActive}
                                onChange={() => {
                                    const descendantCount = getDescendantCategories(category).length;
                                    const newStatus = !category.isActive;
                                    const confirmMsg = `This will ${newStatus ? "activate" : "deactivate"} "${category.name}".${descendantCount ? ` It affects ${descendantCount} sub-categories.` : ""}`;
                                    setConfirmationModal({
                                        isOpen: true,
                                        title: `Confirm ${newStatus ? "Activation" : "Deactivation"}`,
                                        message: confirmMsg,
                                        requireConfirmationText: descendantCount > 0,
                                        onConfirm: async () => {
                                            setConfirmationModal({ ...confirmationModal, isOpen: false });
                                            await toggleCategoryStatus(category, newStatus);
                                        },
                                    });
                                }}
                                disabled={togglingCategory === category._id}
                            />
                        </div>

                        {!reorderMode && permissions.canUpdate && (
                            <button
                                title="Edit"
                                className="p-2 rounded bg-blue-50 hover:bg-blue-100"
                                onClick={() => setEditingCategory({ ...defaultCategory, ...category })}
                            >
                                <Edit size={16} />
                            </button>
                        )}

                        {!reorderMode && permissions.canDelete && (
                            <button
                                title="Delete"
                                className="p-2 rounded bg-red-50 hover:bg-red-100"
                                onClick={() => {
                                    const descendants = getDescendantCategories(category).length;
                                    setConfirmationModal({
                                        isOpen: true,
                                        title: "Confirm Deletion",
                                        message: `Delete "${category.name}"?${descendants ? ` This will also delete ${descendants} sub-categories.` : ""}`,
                                        requireConfirmationText: true,
                                        onConfirm: async () => {
                                            setConfirmationModal({ ...confirmationModal, isOpen: false });
                                            await deleteCategory(category._id);
                                        },
                                    });
                                }}
                            >
                                <Trash2 size={16} />
                            </button>
                        )}
                    </div>
                </div>

                {isExpanded && category.children && category.children.length > 0 && (
                    <div className="pl-4 pb-2">
                        {category.children.map((child) => renderCategory(child, depth + 1))}
                    </div>
                )}
            </div>
        );
    };

    // Main UI
    return (
        <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
            <ConfirmationModal
                isOpen={confirmationModal.isOpen}
                onClose={() => setConfirmationModal({ ...confirmationModal, isOpen: false })}
                onConfirm={confirmationModal.onConfirm}
                title={confirmationModal.title}
                requireConfirmationText={confirmationModal.requireConfirmationText}
            >
                {confirmationModal.message}
            </ConfirmationModal>

            <div className="max-w-6xl mx-auto">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-4 sm:mb-6">
                    <div className="min-w-0">
                        <h1 className="text-xl sm:text-2xl font-bold">Category Management</h1>
                        <p className="text-sm text-gray-600">Organize and manage your product categories</p>
                    </div>

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
                        {permissions.canCreate && (
                            <button
                                onClick={() => {
                                    setIsAddingNew((p) => !p);
                                    setEditingCategory(null);
                                }}
                                className={`flex items-center justify-center gap-2 px-4 py-2 rounded text-sm sm:text-base ${isAddingNew ? "bg-gray-200" : "bg-blue-600 text-white"}`}
                            >
                                <Plus size={16} />
                                {isAddingNew ? "Cancel" : "Add New Category"}
                            </button>
                        )}

                        <button
                            onClick={toggleReorderMode}
                            className={`flex items-center justify-center gap-2 px-4 py-2 rounded text-sm sm:text-base ${reorderMode ? "bg-yellow-500 text-white" : "bg-gray-100"}`}
                        >
                            <Move size={16} />
                            {reorderMode ? "Disable Reorder" : "Enable Reorder"}
                        </button>
                    </div>
                </div>

                {successMessage && (
                    <div className="mb-4 p-3 rounded bg-green-50 border border-green-200 flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <CheckCircle className="text-green-600" />
                            <div className="text-green-800">{successMessage}</div>
                        </div>
                        <button onClick={() => setSuccessMessage("")}>
                            <X size={16} />
                        </button>
                    </div>
                )}

                {error && (
                    <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <AlertCircle className="text-red-600" />
                            <div className="text-red-800">{error}</div>
                        </div>
                        <button onClick={() => setError(null)}>
                            <X size={16} />
                        </button>
                    </div>
                )}

                {/* Add New / Edit form */}
                {(isAddingNew || editingCategory) && (
                    <div className="mb-6 bg-white p-4 rounded shadow">
                        <form onSubmit={editingCategory ? updateCategory : saveNewCategory} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Name</label>
                                <input
                                    className="w-full mt-1 p-2 border rounded"
                                    value={(editingCategory ? editingCategory.name : newCategory.name) || ""}
                                    onChange={(e) => {
                                        if (editingCategory) setEditingCategory({ ...editingCategory, name: e.target.value });
                                        else setNewCategory({ ...newCategory, name: e.target.value });
                                    }}
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Parent Category (optional)</label>
                                <select
                                    className="w-full mt-1 p-2 border rounded"
                                    value={(editingCategory ? editingCategory.parentId || "" : newCategory.parentId || "") || ""}
                                    onChange={(e) => {
                                        const val = e.target.value || null;
                                        if (editingCategory) setEditingCategory({ ...editingCategory, parentId: val });
                                        else setNewCategory({ ...newCategory, parentId: val });
                                    }}
                                >
                                    <option value="">-- Root --</option>
                                    {flattenCategories(categories).map((c) => (
                                        <option key={c._id} value={c._id}>
                                            {c.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">GST Rate (%)</label>
                                <input
                                    type="number"
                                    className="w-full mt-1 p-2 border rounded"
                                    value={(editingCategory ? editingCategory.gstRate : newCategory.gstRate) ?? 0}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (editingCategory) setEditingCategory({ ...editingCategory, gstRate: val });
                                        else setNewCategory({ ...newCategory, gstRate: val });
                                    }}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Image</label>
                                <div className="flex items-center gap-2 mt-1">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        ref={fileInputRef}
                                        onChange={(e) => {
                                            const f = e.target.files && e.target.files[0];
                                            if (!f) return;
                                            const tmpUrl = URL.createObjectURL(f);
                                            if (editingCategory) setEditingCategory({ ...editingCategory, __file: f, imageUrl: tmpUrl });
                                            else setNewCategory({ ...newCategory, __file: f, imageUrl: tmpUrl });
                                        }}
                                    />
                                    <div className="text-sm text-gray-500">Choose image (optional)</div>
                                </div>
                                <div className="mt-2">
                                    {(editingCategory ? editingCategory.imageUrl : newCategory.imageUrl) ? (
                                        <img src={(editingCategory ? editingCategory.imageUrl : newCategory.imageUrl)} alt="preview" className="h-20 w-20 object-cover rounded" />
                                    ) : null}
                                </div>
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">Description</label>
                                <textarea
                                    className="w-full mt-1 p-2 border rounded"
                                    value={(editingCategory ? editingCategory.description : newCategory.description) || ""}
                                    onChange={(e) => {
                                        if (editingCategory) setEditingCategory({ ...editingCategory, description: e.target.value });
                                        else setNewCategory({ ...newCategory, description: e.target.value });
                                    }}
                                />
                            </div>

                            <div className="flex items-center gap-2 md:col-span-2">
                                <button type="submit" className="px-4 py-2 rounded bg-blue-600 text-white">
                                    {editingCategory ? "Save Changes" : "Create Category"}
                                </button>
                                <button
                                    type="button"
                                    className="px-4 py-2 rounded bg-gray-200"
                                    onClick={() => {
                                        setIsAddingNew(false);
                                        setEditingCategory(null);
                                        setNewCategory(defaultCategory);
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                <div className="bg-white p-4 rounded shadow">
                    {reorderingSaving && <div className="mb-3 text-sm text-yellow-700">Saving new order...</div>}

                    {loading && !isAddingNew && !editingCategory ? (
                        <div className="text-center py-8">
                            <div className="animate-spin border-4 border-blue-600 border-t-transparent rounded-full w-8 h-8 mx-auto mb-3" />
                            <div className="text-blue-700">Loading categories...</div>
                        </div>
                    ) : categories.length === 0 ? (
                        <div className="text-center py-6 text-gray-500">No categories found</div>
                    ) : (
                        <div className="space-y-2">
                            {categories.map((cat) => renderCategory(cat))}
                        </div>
                    )}

                    {/* Pagination simple controls */}
                    <div className="mt-4 flex justify-between items-center">
                        <div className="text-sm text-gray-600">
                            Page {pagination.page} of {pagination.totalPages} — {pagination.total} categories
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
                                disabled={pagination.page <= 1}
                                className="px-3 py-1 rounded bg-gray-100 disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => setPagination((p) => ({ ...p, page: Math.min(p.totalPages || 1, p.page + 1) }))}
                                disabled={pagination.page >= (pagination.totalPages || 1)}
                                className="px-3 py-1 rounded bg-gray-100 disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Helper used by form to list parent options (flattened)
function flattenCategories(tree = []) {
    const out = [];
    function walk(items, prefix = "") {
        items.forEach((i) => {
            out.push({ _id: i._id, name: prefix + i.name });
            if (i.children && i.children.length) walk(i.children, prefix + "-- ");
        });
    }
    walk(tree);
    return out;
}
